package com.rainbowclubs.rainbow.presentation.admin

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.rainbowclubs.rainbow.data.models.Membership
import com.rainbowclubs.rainbow.data.models.MembershipStatus
import com.rainbowclubs.rainbow.data.models.UserRole
import com.rainbowclubs.rainbow.presentation.ScreenState
import com.rainbowclubs.rainbow.presentation.common.ErrorScreen
import com.rainbowclubs.rainbow.presentation.common.LoadingScreen
import com.rainbowclubs.rainbow.presentation.theme.RainbowClubTheme
import com.rainbowclubs.rainbow.presentation.theme.transparent
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import rainbowclubs.composeapp.generated.resources.*

@Composable
fun ExistingMembersScreen(
    clubId: String,
    viewModel: ExistingMembersViewModel = koinViewModel(parameters = { parametersOf(clubId) }),
    navigateTo: (ExistingMembersViewModel.NavigationEvent) -> Unit,
) {
    val screenState by viewModel.screenState.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.navigationIntent.collect { intent ->
            navigateTo(intent)
        }
    }

    Scaffold(
        containerColor = MaterialTheme.colorScheme.transparent,
    ) { paddingValues ->
        Box(
            modifier = Modifier
                .padding(paddingValues)
                .fillMaxSize(),
        ) {
            when (val state = screenState) {
                is ScreenState.Loading -> LoadingScreen(Modifier.fillMaxSize())
                is ScreenState.Error -> ErrorScreen(
                    error = stringResource(Res.string.general_error),
                    onButtonClick = viewModel::fetchMembers,
                    buttonText = stringResource(Res.string.try_again_button),
                )

                is ScreenState.Content -> ExistingMembersScreenContent(
                    uiState = state.data,
                    onBackClick = viewModel::onBackClick,
                    onSearchQueryChange = viewModel::onSearchQueryChange,
                    onClearSearch = viewModel::clearSearch,
                    onMemberClick = viewModel::onMemberClick,
                    onLoadMore = viewModel::loadNextPage,
                    onRetrySearch = { viewModel.onSearchQueryChange(state.data.searchQuery) },
                )
            }
        }
    }
}

@Composable
private fun ExistingMembersScreenContent(
    uiState: ExistingMembersViewModel.UiState,
    onBackClick: () -> Unit,
    onSearchQueryChange: (String) -> Unit,
    onClearSearch: () -> Unit,
    onMemberClick: (Membership) -> Unit,
    onLoadMore: () -> Unit,
    onRetrySearch: () -> Unit,
) {
    val listState = rememberLazyListState()
    val shouldLoadMore by remember {
        derivedStateOf {
            val lastVisibleItemIndex = listState.layoutInfo.visibleItemsInfo.lastOrNull()?.index
                ?: return@derivedStateOf false
            val totalItemsCount = listState.layoutInfo.totalItemsCount

            totalItemsCount > 0 && lastVisibleItemIndex >= totalItemsCount - 4
        }
    }

    LaunchedEffect(
        shouldLoadMore,
        uiState.hasNextPage,
        uiState.isLoadingMore,
        uiState.isRefreshing,
        uiState.loadMoreError,
    ) {
        if (
            shouldLoadMore &&
            uiState.hasNextPage &&
            !uiState.isLoadingMore &&
            !uiState.isRefreshing &&
            !uiState.loadMoreError
        ) {
            onLoadMore()
        }
    }

    LazyColumn(
        modifier = Modifier.fillMaxSize(),
        state = listState,
    ) {
        item {
            ExistingMembersHeader(onBackClick = onBackClick)
        }

        item {
            Spacer(modifier = Modifier.height(24.dp))
        }

        item {
            MembersSearchField(
                query = uiState.searchQuery,
                onQueryChange = onSearchQueryChange,
                onClearSearch = onClearSearch,
            )
        }

        item {
            Spacer(modifier = Modifier.height(24.dp))
        }

        when {
            uiState.isRefreshing -> {
                item {
                    SearchLoadingState()
                }
            }

            uiState.refreshError -> {
                item {
                    RefreshMembersError(onRetrySearch = onRetrySearch)
                }
            }

            uiState.members.isEmpty() -> {
                item {
                    EmptyMembersState(hasSearchQuery = uiState.searchQuery.isNotBlank())
                }
            }

            else -> {
                itemsIndexed(
                    items = uiState.members,
                    key = { index, member -> member.membershipId.ifBlank { "${member.userId}-$index" } },
                ) { _, member ->
                    ExistingMemberCard(
                        member = member,
                        onClick = { onMemberClick(member) },
                    )
                    Spacer(modifier = Modifier.height(16.dp))
                }

                if (uiState.isLoadingMore || uiState.loadMoreError) {
                    item {
                        LoadMoreMembersFooter(
                            isLoading = uiState.isLoadingMore,
                            hasError = uiState.loadMoreError,
                            onRetry = onLoadMore,
                        )
                    }
                }
            }
        }

        item {
            Spacer(modifier = Modifier.height(28.dp))
        }
    }
}

@Composable
private fun ExistingMembersHeader(onBackClick: () -> Unit) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        TextButton(
            onClick = onBackClick,
            modifier = Modifier.size(40.dp),
            contentPadding = PaddingValues(0.dp),
        ) {
            Icon(
                painter = painterResource(Res.drawable.ic_arrow_back),
                contentDescription = stringResource(Res.string.back_icon_cd),
                modifier = Modifier.size(28.dp),
                tint = MaterialTheme.colorScheme.onBackground,
            )
        }

        Spacer(modifier = Modifier.width(20.dp))

        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = stringResource(Res.string.existing_members),
                color = MaterialTheme.colorScheme.onBackground,
                style = MaterialTheme.typography.titleLarge,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )

            Spacer(modifier = Modifier.height(8.dp))

            Text(
                text = stringResource(Res.string.existing_members_page_description),
                color = MaterialTheme.colorScheme.outline,
                style = MaterialTheme.typography.titleMedium,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
        }
    }
}

@Composable
private fun MembersSearchField(
    query: String,
    onQueryChange: (String) -> Unit,
    onClearSearch: () -> Unit,
) {
    OutlinedTextField(
        value = query,
        onValueChange = onQueryChange,
        modifier = Modifier.fillMaxWidth(),
        singleLine = true,
        textStyle = MaterialTheme.typography.titleMedium,
        shape = RoundedCornerShape(18.dp),
        leadingIcon = {
            Icon(
                painter = painterResource(Res.drawable.ic_search),
                contentDescription = stringResource(Res.string.search_icon_cd),
                modifier = Modifier.size(22.dp),
                tint = MaterialTheme.colorScheme.outline,
            )
        },
        trailingIcon = {
            if (query.isNotBlank()) {
                IconButton(onClick = onClearSearch) {
                    Icon(
                        painter = painterResource(Res.drawable.ic_close),
                        contentDescription = stringResource(Res.string.clear_search_icon_cd),
                        modifier = Modifier.size(20.dp),
                        tint = MaterialTheme.colorScheme.outline,
                    )
                }
            }
        },
        placeholder = {
            Text(
                text = stringResource(Res.string.search_members),
                color = MaterialTheme.colorScheme.outline,
                style = MaterialTheme.typography.titleMedium,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
        },
    )
}

@Composable
private fun ExistingMemberCard(
    member: Membership,
    onClick: () -> Unit,
) {
    val displayName = member.displayName()

    Surface(
        onClick = onClick,
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(24.dp),
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outline.copy(alpha = 0.42f)),
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 16.dp, vertical = 16.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            MemberAvatar(displayName = displayName)

            Spacer(modifier = Modifier.width(16.dp))

            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = displayName,
                    color = MaterialTheme.colorScheme.onSurface,
                    style = MaterialTheme.typography.titleLarge,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )

                Spacer(modifier = Modifier.height(8.dp))

                Row(
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Icon(
                        painter = painterResource(Res.drawable.ic_calendar),
                        contentDescription = null,
                        modifier = Modifier.size(18.dp),
                        tint = MaterialTheme.colorScheme.outline,
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(
                        text = stringResource(
                            Res.string.requested_at_value,
                            member.requestedAt.requestedDateLabel(),
                        ),
                        color = MaterialTheme.colorScheme.outline,
                        style = MaterialTheme.typography.titleMedium,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                    )
                }
            }
        }
    }
}

@Composable
private fun MemberAvatar(displayName: String) {
    val accent = MaterialTheme.colorScheme.primary

    Box(
        modifier = Modifier
            .size(54.dp)
            .background(accent.copy(alpha = 0.12f), CircleShape),
        contentAlignment = Alignment.Center,
    ) {
        Text(
            text = displayName.initials(),
            color = accent,
            style = MaterialTheme.typography.titleLarge,
            maxLines = 1,
        )
    }
}

@Composable
private fun SearchLoadingState() {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .padding(top = 92.dp),
        contentAlignment = Alignment.Center,
    ) {
        CircularProgressIndicator(
            modifier = Modifier.size(32.dp),
            strokeWidth = 3.dp,
        )
    }
}

@Composable
private fun RefreshMembersError(onRetrySearch: () -> Unit) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(16.dp),
        color = MaterialTheme.colorScheme.errorContainer,
        contentColor = MaterialTheme.colorScheme.onErrorContainer,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.error.copy(alpha = 0.28f)),
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 16.dp, vertical = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                text = stringResource(Res.string.members_refresh_error),
                modifier = Modifier.weight(1f),
                style = MaterialTheme.typography.titleMedium,
            )
            TextButton(onClick = onRetrySearch) {
                Text(
                    text = stringResource(Res.string.try_again_button),
                    style = MaterialTheme.typography.titleMedium,
                )
            }
        }
    }
}

@Composable
private fun EmptyMembersState(hasSearchQuery: Boolean) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(top = 92.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        EmptyMembersIcon(hasSearchQuery = hasSearchQuery)

        Spacer(modifier = Modifier.height(24.dp))

        Text(
            text = stringResource(
                if (hasSearchQuery) {
                    Res.string.no_matching_members
                } else {
                    Res.string.no_existing_members
                }
            ),
            color = MaterialTheme.colorScheme.onSurface,
            style = MaterialTheme.typography.titleLarge,
            textAlign = TextAlign.Center,
        )

        Spacer(modifier = Modifier.height(8.dp))

        Text(
            text = stringResource(
                if (hasSearchQuery) {
                    Res.string.no_matching_members_description
                } else {
                    Res.string.no_existing_members_description
                }
            ),
            color = MaterialTheme.colorScheme.outline,
            style = MaterialTheme.typography.titleMedium,
            textAlign = TextAlign.Center,
        )
    }
}

@Composable
private fun EmptyMembersIcon(hasSearchQuery: Boolean) {
    val accent = MaterialTheme.colorScheme.primary

    Surface(
        modifier = Modifier.size(86.dp),
        shape = CircleShape,
        color = accent.copy(alpha = 0.12f),
        border = BorderStroke(1.dp, accent.copy(alpha = 0.32f)),
    ) {
        Box(contentAlignment = Alignment.Center) {
            Icon(
                painter = painterResource(
                    if (hasSearchQuery) {
                        Res.drawable.ic_search
                    } else {
                        Res.drawable.ic_clubs
                    }
                ),
                contentDescription = null,
                modifier = Modifier.size(42.dp),
                tint = accent,
            )
        }
    }
}

@Composable
private fun LoadMoreMembersFooter(
    isLoading: Boolean,
    hasError: Boolean,
    onRetry: () -> Unit,
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 8.dp),
        contentAlignment = Alignment.Center,
    ) {
        when {
            isLoading -> Row(
                verticalAlignment = Alignment.CenterVertically,
            ) {
                CircularProgressIndicator(
                    modifier = Modifier.size(20.dp),
                    strokeWidth = 2.dp,
                    color = MaterialTheme.colorScheme.primary,
                )
                Spacer(modifier = Modifier.width(10.dp))
                Text(
                    text = stringResource(Res.string.loading_more_members),
                    color = MaterialTheme.colorScheme.outline,
                    style = MaterialTheme.typography.titleMedium,
                )
            }

            hasError -> TextButton(onClick = onRetry) {
                Text(
                    text = stringResource(Res.string.members_load_more_error),
                    style = MaterialTheme.typography.titleMedium,
                )
            }
        }
    }
}

private fun Membership.displayName(): String {
    return userDisplayName.ifBlank { userId }
}

private fun String.initials(): String {
    val parts = trim().split(" ").filter { it.isNotBlank() }
    return parts.take(2).joinToString("") { it.first().uppercase() }.ifBlank { "?" }
}

private fun String?.requestedDateLabel(): String {
    val value = this?.trim().orEmpty()
    if (value.isBlank()) return "-"

    return value.substringBefore("T")
}

@Preview(name = "Existing Members - List")
@Composable
private fun ExistingMembersListPreview() {
    RainbowClubTheme {
        Surface {
            ExistingMembersScreenContent(
                uiState = ExistingMembersViewModel.UiState(
                    members = sampleExistingMembers(),
                    hasNextPage = true,
                ),
                onBackClick = {},
                onSearchQueryChange = {},
                onClearSearch = {},
                onMemberClick = {},
                onLoadMore = {},
                onRetrySearch = {},
            )
        }
    }
}

@Preview(name = "Existing Members - Empty")
@Composable
private fun ExistingMembersEmptyPreview() {
    RainbowClubTheme {
        Surface {
            ExistingMembersScreenContent(
                uiState = ExistingMembersViewModel.UiState(),
                onBackClick = {},
                onSearchQueryChange = {},
                onClearSearch = {},
                onMemberClick = {},
                onLoadMore = {},
                onRetrySearch = {},
            )
        }
    }
}

@Preview(name = "Existing Members - No Search Results")
@Composable
private fun ExistingMembersNoSearchResultsPreview() {
    RainbowClubTheme {
        Surface {
            ExistingMembersScreenContent(
                uiState = ExistingMembersViewModel.UiState(searchQuery = "alex"),
                onBackClick = {},
                onSearchQueryChange = {},
                onClearSearch = {},
                onMemberClick = {},
                onLoadMore = {},
                onRetrySearch = {},
            )
        }
    }
}

private fun sampleExistingMembers(): List<Membership> {
    return listOf(
        Membership(
            userDisplayName = "Alex Doe",
            userId = "alex@example.com",
            clubName = "Rainbow Club",
            clubId = "club-1",
            membershipId = "membership-1",
            role = UserRole.MEMBER,
            status = MembershipStatus.APPROVED,
            requestedAt = "2026-06-17T10:15:30Z",
        ),
        Membership(
            userDisplayName = "Mina Patel",
            userId = "mina@example.com",
            clubName = "Rainbow Club",
            clubId = "club-1",
            membershipId = "membership-2",
            role = UserRole.ADMIN,
            status = MembershipStatus.APPROVED,
            requestedAt = "2026-06-14T08:20:00Z",
        ),
        Membership(
            userDisplayName = "Sam Rivera",
            userId = "sam@example.com",
            clubName = "Rainbow Club",
            clubId = "club-1",
            membershipId = "membership-3",
            role = UserRole.MEMBER,
            status = MembershipStatus.BLOCKED,
            requestedAt = "2026-06-12T17:10:00Z",
        ),
    )
}
